import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { SelectChangeEvent } from '@mui/material/Select'; // Import the correct event type
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { format, formatDistance } from 'date-fns';
import { Helmet } from 'react-helmet';
import { Box, Skeleton } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/system';
import config from '@configFile';
import trackUse from '@utils/trackUse';
import useLocalStorage from '@hooks/useLocalStorage';
import ScrollTop from '@components/ScrollTop';
import { fixCategory } from '@utils/categories';
import { faqs } from '@components/SubscribeSaveExplanation';
import {
  DealPostType,
  OptionType,
  PageDealType,
  DealPageSEOType,
  DEALS_UPDATE_DEAL_PAGE_SEO
} from '@types';
import {
  logPostHogEvent,
  getTitleFromPageDeal,
  getPostPrefix,
  getDescriptionFromPageDeal,
  getHowManyExtraDealsByExtraDealLink,
  getHowManyDealsByBrand,
  getHowManyDealsByPromoCode,
  categoryAll,
  ensureWebP
} from '@utils/index';
import ShareButton from '@components/ShareButton';
import PageContainer from '@components/PageContainer';
import AdminSlugActions from '@components/AdminSlugActions';
import DealCard from '@components/DealCard';
import Disclaimer from '@components/Disclaimer';
import { useGetUserData } from '@hooks/useGetUserData';
import EmailSignUp from '@components/EmailSignUp';
import {
  seoOrganization,
  seoWebsite,
  authorSchema,
  getPageImageSeo
} from '@utils/seo';
import LoadingSkeletons from '@pages/Pages/components/LoadingSkeletons';
import Description from '@pages/Pages//components/Description';
import {
  getSEOMeta,
  getDealCtString,
  getDealCountString,
  getDealCountStringNoNum,
  getDealStr
} from '@pages/Pages/utils';
import FilterByPromotionChip from '@pages/Pages/components/FilterByPromotionChip';
import FilterByBrandChip from '@pages/Pages/components/FilterByBrandChip';
import FilterByCouponChip from '@pages/Pages/components/FilterByCouponChip';
import StickyFilters from '@pages/Pages/components/StickyFilters';
import SearchComponent from '@pages/Pages/components/SearchComponent';
import NoDealsFoundMessage from '@pages/Pages/components/NoDealsFoundMessage';
import MainPageImage from '@pages/Pages/components/MainPageImage';
import NoDealsFilterMessage from '@pages/Pages/components/NoDealsFilterMessage';
import FilterDisclaimerLoading from '@pages/Pages/components/FilterDisclaimerLoading';

import useFilteredSortedDeals, {
  defaultCategories,
  isSubscribeAndSavePage,
  isPromotionsPage,
  isDiapersPage
} from '@pages/Pages/useFilterSortedDeals';
import PosterDetails from '@components/PosterDetails';
import PageContent from '@pages/Pages/components/PageContent';
import PaginationBottom from '@pages/Pages/components/PaginationBottom';
import PaginationTop from '@pages/Pages/components/PaginationTop';

const DealPostToProductSchema = (deal: DealPostType, url: string) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: deal.title,
    ...(!deal.brand
      ? {}
      : {
          brand: {
            '@type': 'Brand',
            name: deal.brand
          }
        }),
    ...(!deal?.image600Width
      ? {}
      : {
          image: {
            '@type': 'ImageObject',
            url: (deal?.image600Url || '')?.replace('jpg', 'webp'),
            height: deal?.image600Height,
            width: deal?.image600Width
          }
        }),
    offers: [
      {
        '@type': 'Offer',
        name: deal.title,
        description: deal.postText,
        // "category": {
        //   "@type": "Thing",
        //   "name": "Groceries & Essentials / Household Essentials",
        //   "url": "https://categoryurl.com"
        // },
        availability: 'https://schema.org/InStock',
        price: deal.finalPrice,
        priceCurrency: 'USD',
        url: `${config.SITE_URL}/blog/${deal.slug}`,
        seller: {
          '@type': 'Organization',
          name: 'Amazon',
          url: `${url}`
        }
      }
    ]
  };
};

const productsToSchema = (deals: DealPostType[], url: string) => {
  // only the top 20 deals
  return deals.slice(0, 20).map((deal) => DealPostToProductSchema(deal, url));
};

const dealContainerStyle = {
  containerType: 'inline-size',
  padding: {
    xs: '0px',
    md: '20px 64px',
    xl: '20px 94px'
  },
  backgroundColor: {
    xs: 'transparent'
  },
  margin: {
    xs: '0px',
    md: '8px -16px 0px -16px'
  }
};

const innerDealContainerStyle = {
  backgroundColor: {
    xs: 'transparent'
  },
  borderRadius: '4px'
};

const PageDeal = ({
  pageDealData,
  dealPageSEO,
  onDealsLoaded,
  customTitle
}: {
  pageDealData: {
    pageDeal: PageDealType;
    deals: DealPostType[];
  } | null;
  dealPageSEO: DealPageSEOType | null;
  onDealsLoaded: () => void;
  customTitle?: string;
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const initialLimit = 4;
  const minDealsToPaginate = 10;
  const defaultAmountToPaginate = 30;
  const maxItemsPerPage = 100;
  const [itemsPerPageValue, setItemsPerPage] = useLocalStorage(
    'deals-to-paginate',
    defaultAmountToPaginate
  ); // Default rows per page
  const itemsPerPage = Math.min(itemsPerPageValue, maxItemsPerPage);
  const [limit, setLimit] = useState<number | null>(initialLimit);
  const topOfList = useRef<HTMLDivElement | null>(null);
  const { data: user } = useGetUserData();
  const showBrandFilter = pageDealData?.pageDeal?.showBrandFilter || false;
  const showCouponFilter = pageDealData?.pageDeal?.showCouponFilter || false;
  const [page, setPage] = useState(1);
  const [hasCalledCallback, setHasCalledCallback] = useState(false);
  const [promotionFilter, setPromotionFilter] = useState('all');
  const [couponFilter, setCouponFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('ALL_BRANDS');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('dateActive');
  const [categoryFilters, setCategoryFilters] =
    useState<OptionType[]>(defaultCategories);
  const [searchTerm, setSearchTerm] = useState('');
  const [visualFilter, setVisualFilter] = useState('');

  const isLoading = !pageDealData || !Array.isArray(pageDealData?.deals);
  const noDeals = !isLoading && pageDealData?.deals?.length === 0;
  const ss = pageDealData?.deals || [];
  const totalDeals = (ss || [])?.length || 0;
  const slug = pageDealData?.pageDeal?.slug || dealPageSEO?.slug || '';
  const isSubscribeAndSave = isSubscribeAndSavePage(slug);
  const isPromotions = isPromotionsPage(slug);
  const isDiapers = isDiapersPage(slug);

  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchTerm(query);
      trackUse({
        item: `page-deal-search`,
        value: `${query} — https://www.jungle.deals/p/${
          pageDealData?.pageDeal?.slug || dealPageSEO?.slug
        }`,
        type: 'SEARCH'
      });
      setPage(1);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      dispatch({
        type: DEALS_UPDATE_DEAL_PAGE_SEO,
        dealPageSEO: null
      });
    };
  }, [dispatch]);

  const scrollToTop = () =>
    setTimeout(() => {
      topOfList.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);

  const handleSortChange = (value: string) => {
    trackUse({
      item: `page-deal-sort-change`,
      value: `${value} — https://www.jungle.deals/p/${
        pageDealData?.pageDeal?.slug || dealPageSEO?.slug
      }`,
      type: 'ACTION'
    });

    logPostHogEvent('page-deal-sort-change', {
      value: `https://www.jungle.deals/p/${
        pageDealData?.pageDeal?.slug || dealPageSEO?.slug
      }`
    });

    setPage(1);
    scrollToTop();
    setSortOrder(value);
  };

  const filteredItems = useFilteredSortedDeals(
    ss,
    categoryFilters,
    sortOrder,
    searchTerm,
    promotionFilter,
    couponFilter,
    brandFilter,
    sizeFilter,
    isPromotions,
    isDiapers,
    showBrandFilter,
    pageDealData?.pageDeal?.showCategoryFilter,
    showCouponFilter
  );

  const categories = useMemo(() => {
    const data = (ss || []).reduce(
      (acc, deal) => {
        if (deal.category) {
          const category = fixCategory(deal.category);
          acc[category] = (acc[category] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(data).sort((a, b) => b[1] - a[1]);
  }, [ss]);

  const today = format(new Date(), "'Updated' LLL do, yyyy");

  const getCategoryFromPageDeal = useCallback(
    (pageDeal: PageDealType | null) => {
      return isSubscribeAndSave ? 'Subscribe & Save' : pageDeal?.category || '';
    },
    [isSubscribeAndSave]
  );

  const dealCountStr = useMemo(
    () => getDealCtString(ss.length),
    [ss.length, getCategoryFromPageDeal, pageDealData]
  );

  const dealCountLongStr = useMemo(
    () =>
      getDealCountString(
        ss.length,
        getCategoryFromPageDeal(pageDealData?.pageDeal)
      ),
    [ss.length, getCategoryFromPageDeal, pageDealData]
  );

  const dealStrNoNumLong = useMemo(
    () =>
      getDealCountStringNoNum(
        ss.length,
        getCategoryFromPageDeal(pageDealData?.pageDeal)
      ),
    [ss.length, getCategoryFromPageDeal, pageDealData]
  );

  const dealStr = useMemo(
    () =>
      getDealStr(ss.length, getCategoryFromPageDeal(pageDealData?.pageDeal)),
    [ss.length, getCategoryFromPageDeal, pageDealData]
  );

  const pageDealTitle =
    customTitle ||
    getTitleFromPageDeal(pageDealData?.pageDeal) ||
    dealPageSEO?.title ||
    '';
  const titleNoDate = `${
    pageDealTitle ? `${getPostPrefix()}${pageDealTitle}` : ''
  }`;
  const title = `${pageDealTitle ? `${titleNoDate} — ` : ''}${today}`;

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const totalItems = filteredItems.length;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const toDisplay = filteredItems.slice(startIndex, endIndex);
  const content = useMemo(() => {
    return pageDealData?.pageDeal?.content || dealPageSEO?.content || [];
  }, [pageDealData, dealPageSEO]);

  const shouldShowFilters = useMemo(
    () =>
      pageDealData?.pageDeal?.showCategoryFilter ||
      pageDealData?.pageDeal?.showSearch ||
      pageDealData?.pageDeal?.showSort,
    [pageDealData]
  );

  const pageDealDesc = pageDealData?.pageDeal
    ? getDescriptionFromPageDeal(pageDealData?.pageDeal)
    : null;
  const description = pageDealDesc || dealPageSEO?.description || '';

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    trackUse({
      item: 'page-change',
      value: `Page: ${value} Items PP: ${itemsPerPage} Slug: ${slug}`,
      type: 'CLICK'
    });
    scrollToTop();
  };

  const seoImage = useMemo(
    () =>
      (
        pageDealData?.pageDeal?.seoImage ||
        dealPageSEO?.seoImage ||
        pageDealData?.pageDeal?.collageImageSmall ||
        pageDealData?.pageDeal?.imageSmall ||
        dealPageSEO?.collageImageSmall ||
        dealPageSEO?.imageSmall ||
        ''
      )
        .replace('http:', 'https:')
        .replace('.webp', '.png'),
    [pageDealData, dealPageSEO]
  );

  const seoImageHeight = useMemo(() => {
    if (pageDealData?.pageDeal?.seoImage || dealPageSEO?.seoImage) {
      return 630;
    }

    return (
      pageDealData?.pageDeal?.collageImageSmallHeight ||
      dealPageSEO?.collageImageSmallHeight ||
      400
    );
  }, [pageDealData, dealPageSEO]);

  const seoImageWidth = useMemo(() => {
    if (pageDealData?.pageDeal?.seoImage || dealPageSEO?.seoImage) {
      return 1200;
    }

    return (
      pageDealData?.pageDeal?.collageImageSmallWidth ||
      dealPageSEO?.collageImageSmallWidth ||
      800
    );
  }, [pageDealData, dealPageSEO]);

  const collageImage = useMemo(() => {
    if (!pageDealData?.pageDeal?.collageImage) {
      return (dealPageSEO?.collageImage || '').replace('http:', 'https:');
    }

    return (
      pageDealData?.pageDeal?.collageImage ||
      dealPageSEO?.collageImage ||
      ''
    ).replace('http:', 'https:');
  }, [dealPageSEO, pageDealData]);

  const collageImageSmall = useMemo(() => {
    if (!pageDealData?.pageDeal?.collageImageSmall) {
      return (dealPageSEO?.collageImageSmall || '').replace('http:', 'https:');
    }

    return (
      pageDealData?.pageDeal?.collageImageSmall ||
      dealPageSEO?.collageImageSmall ||
      ''
    ).replace('http:', 'https:');
  }, [dealPageSEO, pageDealData]);

  const imageLarge = useMemo(() => {
    if (!pageDealData?.pageDeal?.imageLarge) {
      return (dealPageSEO?.imageLarge || '').replace('http:', 'https:');
    }

    return (
      pageDealData?.pageDeal?.imageLarge ||
      dealPageSEO?.imageLarge ||
      ''
    ).replace('http:', 'https:');
  }, [dealPageSEO, pageDealData]);

  useEffect(() => {
    if (!isLoading && !hasCalledCallback) {
      onDealsLoaded();
      setHasCalledCallback(true);
    }
  }, [isLoading, onDealsLoaded, hasCalledCallback]);

  const filteredString = useMemo(
    () =>
      (isPromotions && promotionFilter !== 'all') ||
      (showCouponFilter && couponFilter !== 'all') ||
      (showBrandFilter && brandFilter !== 'ALL_BRANDS') ||
      searchTerm !== '' ||
      !(
        categoryFilters.length === 1 &&
        categoryFilters?.[0].value === categoryAll.value
      ) ||
      (isDiapers && sizeFilter !== 'all')
        ? {
            short: `${filteredItems.length.toLocaleString()} ${dealStr}`,
            long: `${filteredItems.length.toLocaleString()} Filtered ${dealStrNoNumLong}`
          }
        : {},
    [
      isPromotions,
      promotionFilter,
      showCouponFilter,
      couponFilter,
      showBrandFilter,
      showCouponFilter,
      brandFilter,
      searchTerm,
      categoryFilters,
      isDiapers,
      sizeFilter,
      filteredItems.length,
      dealStr
    ]
  );

  const isFiltered = !!filteredString?.short || false;
  const hasNoItems =
    filteredItems?.length === 0 && !isLoading && ss?.length > 0;

  const hasPart = (dealPageSEO?.deals || []).map((deal) => ({
    '@type': 'Offer',
    name: deal?.title,
    url: `https://www.amazon.com/dp/${deal.ASIN}?tag=${config.AFFILIATE_TAGS.LISTS}`,
    price: `${deal.finalPrice}`,
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    image: deal.image
  }));

  const isSSPage = dealPageSEO?.slug === 'best-subscribe-save-deals-amazon';

  const loadingTitle = customTitle ? title : `Loading...`;
  const titleForPage = pageDealTitle ? title : loadingTitle;
  const link = isSSPage
    ? `${config.SITE_URL}/best-amazon-subscribe-save-deals`
    : `${config.SITE_URL}/p/${slug}`;

  const currentUrl = link;
  const jsonLd: any = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${currentUrl}`,
        url: `${currentUrl}`,
        name: `${title}`,
        isPartOf: {
          '@id': `${config.SITE_URL}/#website`
        },
        primaryImageOfPage: {
          '@id': `${currentUrl}/#primaryimage`
        },
        image: {
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          '@id': `${currentUrl}/#primaryimage`,
          url: `${seoImage}`,
          width: seoImageWidth,
          height: seoImageHeight
        },
        datePublished: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        description: description
          ? `${description?.slice(0, 130)?.trim()}...`
          : '',
        breadcrumb: {
          '@id': `${currentUrl}/#breadcrumb`
        },
        inLanguage: 'en-US',
        author: {
          '@type': 'Person',
          '@id': `${config.SITE_URL}${config.admin.authorLink}`
        },
        potentialAction: [
          {
            '@type': 'ReadAction',
            target: [`${currentUrl}`]
          }
        ],
        ...(hasPart.length > 0 ? { hasPart: [...hasPart] } : {})
      },
      {
        ...getPageImageSeo(seoImage, currentUrl)
      },
      ...productsToSchema(ss, link),
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${currentUrl}/#breadcrumb`,
        name: `${title}`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${config.SITE_URL}`
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${config.SITE_URL}/blog`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: `${title}`,
            item: `${currentUrl}`
          }
        ]
      },
      {
        ...seoWebsite
      },
      {
        ...seoOrganization
      },
      {
        ...authorSchema
      }
    ]
  };

  if (isSSPage && faqs.length > 0) {
    jsonLd['@graph'].push({
      '@type': 'FAQPage',
      '@id': `${currentUrl}/#faq`,
      name: 'Amazon Subscribe & Save FAQs',
      description:
        'Frequently asked questions about Amazon Subscribe & Save Deals',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.plainAnswer || faq.answer
        }
      }))
    });
  }

  const links: {
    rel: string;
    href: string;
    as?: string;
  }[] = [
    {
      rel: 'canonical',
      href: link
    }
  ];

  if (dealPageSEO?.collageImage) {
    links.push({
      rel: 'preload',
      as: 'image',
      href: ensureWebP(dealPageSEO?.collageImage)
    });
  }

  if (dealPageSEO?.collageImageSmall) {
    links.push({
      rel: 'preload',
      as: 'image',
      href: ensureWebP(dealPageSEO?.collageImageSmall)
    });
  }

  if (imageLarge) {
    links.push({
      rel: 'preload',
      as: 'image',
      href: ensureWebP(imageLarge)
    });
  }

  const renderDisclaimer = () => {
    if (isLoading) {
      return <FilterDisclaimerLoading />;
    }

    return (
      <Box marginTop="8px">
        <Disclaimer />
      </Box>
    );
  };

  const handleChangeCategoryFilter = (cats: OptionType[]) => {
    setCategoryFilters(cats);
    scrollToTop();
    setPage(1);
  };

  const getScriptForSEO = () => {
    return [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLd)
      }
    ];
  };

  const getSEOMetaFunc = () => {
    return getSEOMeta({
      image: seoImage,
      slug: pageDealData?.pageDeal?.slug || dealPageSEO?.slug,
      description: `${description?.slice(0, 130)?.trim()}...`,
      title
    });
  };

  const renderShareOrAdminButton = () => {
    if (!isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="flex-end"
          position="absolute"
          right="8px"
          top="8px"
        >
          {user?.isAdmin ? (
            <AdminSlugActions pageDeal={pageDealData?.pageDeal} />
          ) : (
            <ShareButton title={title} url={link} />
          )}
        </Box>
      );
    }

    return null;
  };

  const renderNotFoundMessage = () => {
    if (!isLoading && noDeals) {
      return <NoDealsFoundMessage />;
    }

    return null;
  };

  const renderResetFilters = () => {
    if (isFiltered && hasNoItems) {
      return (
        <NoDealsFilterMessage
          resetFilters={() => {
            setCategoryFilters(defaultCategories);
            setPromotionFilter('all');
            setCouponFilter('all');
            setBrandFilter('ALL_BRANDS');
            setSizeFilter('all');
            setVisualFilter('');
            setSearchTerm('');
            setPage(1);
            scrollToTop();
          }}
        />
      );
    }

    return null;
  };

  const renderPageContent = () => {
    return <PageContent content={content} currentUrl={currentUrl} />;
  };

  const renderDescription = () => {
    return <Description description={description} isLoading={isLoading} />;
  };

  const renderImage = () => {
    return (
      <MainPageImage
        collageImage={ensureWebP(collageImage)}
        collageImageSmall={ensureWebP(collageImageSmall)}
        imageLarge={imageLarge}
        isLoading={isLoading}
      />
    );
  };

  const renderFilters = () => {
    if (!isLoading && shouldShowFilters) {
      return (
        <StickyFilters
          pageDealData={pageDealData}
          categories={categories}
          sortOrder={sortOrder}
          setSortOrder={handleSortChange}
          categoryFilters={categoryFilters}
          handleChangeCategoryFilter={handleChangeCategoryFilter}
          renderSearch={() => (
            <SearchComponent
              visualFilter={visualFilter}
              setVisualFilter={setVisualFilter}
              debouncedSearch={debouncedSearch}
              clearSearch={() => {
                setVisualFilter('');
                setSearchTerm('');
                scrollToTop();
                setPage(1);
              }}
            />
          )}
          isPromotions={isPromotions}
          setPromotionFilter={setPromotionFilter}
          setCouponFilter={setCouponFilter}
          couponFilter={couponFilter}
          promotionFilter={promotionFilter}
          deals={pageDealData?.deals || []}
          showBrandFilter={showBrandFilter}
          showCouponFilter={showCouponFilter}
          setBrandFilter={setBrandFilter}
          brandFilter={brandFilter}
          isDiapers={isDiapers}
          setSizeFilter={setSizeFilter}
          sizeFilter={sizeFilter}
          scrollToTop={scrollToTop}
          setLimit={setLimit}
        />
      );
    }

    return null;
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const perPage = Number(event.target.value);
    setItemsPerPage(perPage);
    setPage(1); // Reset to the first page when rows per page changes
    trackUse({
      item: 'items-per-page-change',
      value: `Items PP: ${perPage} Slug: ${slug}`,
      type: 'CLICK'
    });
  };

  const showPaginationBottom = () => {
    return (
      <PaginationBottom
        totalDeals={totalDeals}
        minDealsToPaginate={minDealsToPaginate}
        totalPages={totalPages}
        page={page}
        isFiltered={isFiltered}
        hasNoItems={hasNoItems}
        handlePageChange={handlePageChange}
      />
    );
  };

  const showPagination = () => {
    return (
      <PaginationTop
        totalDeals={totalDeals}
        minDealsToPaginate={minDealsToPaginate}
        startIndex={startIndex}
        endIndex={endIndex}
        itemsPerPage={itemsPerPage}
        filteredString={filteredString}
        dealCountStr={dealCountStr}
        dealCountLongStr={dealCountLongStr}
        isFiltered={isFiltered}
        hasNoItems={hasNoItems}
        handleItemsPerPageChange={handleItemsPerPageChange}
      />
    );
  };

  const renderDeals = () => {
    if (!isLoading && noDeals) {
      return null;
    }

    return (
      <>
        {showPagination()}
        <Box
          sx={dealContainerStyle}
          maxWidth={config.MAX_WIDTH_DEAL_CARD}
          margin="0 auto 44px auto"
        >
          <Box sx={innerDealContainerStyle}>
            {isLoading ? <LoadingSkeletons amount={itemsPerPage} /> : null}

            {toDisplay.length > 0
              ? map(toDisplay, (pageDealItem, i) => {
                  const renderExtraComponent = () => {
                    if (isPromotions) {
                      return (
                        <FilterByPromotionChip
                          setPromotionFilter={(extraDealLink: string) => {
                            setPromotionFilter(extraDealLink);
                            scrollToTop();
                            setLimit(100);
                            setPage(1);
                          }}
                          promotionFilter={promotionFilter}
                          promotionAmount={getHowManyExtraDealsByExtraDealLink(
                            ss,
                            pageDealItem.extraDealLink
                          )}
                          {...pageDealItem}
                        />
                      );
                    }

                    if (showBrandFilter) {
                      return (
                        <FilterByBrandChip
                          setBrandFilter={(brand: string) => {
                            setBrandFilter(brand);
                            setLimit(100);
                            setPage(1);
                            scrollToTop();
                          }}
                          brandAmount={getHowManyDealsByBrand(
                            ss,
                            pageDealItem.brand
                          )}
                          brandFilter={brandFilter}
                          {...pageDealItem}
                        />
                      );
                    }

                    if (showCouponFilter) {
                      return (
                        <FilterByCouponChip
                          setCouponFilter={(brand: string) => {
                            setCouponFilter(brand);
                            setLimit(100);
                            scrollToTop();
                            setPage(1);
                          }}
                          couponAmount={getHowManyDealsByPromoCode(
                            ss,
                            pageDealItem.promoCode
                          )}
                          couponFilter={couponFilter}
                          {...pageDealItem}
                        />
                      );
                    }

                    return null;
                  };

                  return (
                    <DealCard
                      {...pageDealItem}
                      isFirst={i === 0}
                      key={pageDealItem.ASIN}
                      shouldNotLinkSubscribeSave={!!isSubscribeAndSave}
                      shouldLazyLoad={limit === null}
                      tag={
                        pageDealData?.pageDeal?.tag ||
                        config?.AFFILIATE_TAGS?.LISTS
                      }
                      extraLine={
                        isSubscribeAndSave && pageDealItem.dateFirstActive
                          ? `Added to Subscribe & Save deals  
                    ${formatDistance(
                      new Date(pageDealItem.dateFirstActive),
                      new Date(),
                      {
                        addSuffix: true
                      }
                    )}`
                          : undefined
                      }
                      extraComponent={renderExtraComponent()}
                    />
                  );
                })
              : null}
          </Box>
        </Box>
        {showPaginationBottom()}
      </>
    );
  };

  return (
    <PageContainer fullWidth title={titleForPage}>
      <Helmet
        title={titleNoDate}
        meta={getSEOMetaFunc()}
        link={links}
        script={getScriptForSEO()}
      />
      <Box
        sx={{
          backgroundColor: theme.palette.secondaryBackground,
          margin: '0px -16px 16px -16px',
          padding: '10px 17px',
          boxShadow: 1
        }}
      >
        {isLoading ? (
          <Skeleton height="21px" width="276px" />
        ) : (
          <PosterDetails />
        )}
      </Box>
      {renderShareOrAdminButton()}
      {renderImage()}
      {renderDescription()}
      <Box ref={topOfList} />
      {renderFilters()}
      {renderDisclaimer()}
      {renderDeals()}
      {renderResetFilters()}
      {renderNotFoundMessage()}
      {renderPageContent()}
      {pageDealData?.pageDeal?.mailingListSignUpUrl && (
        <EmailSignUp
          mailingListId={pageDealData?.pageDeal?.mailingListSignUpUrl}
          showCard
        />
      )}
      <ScrollTop />
    </PageContainer>
  );
};

export default PageDeal;
